"use client"

import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../components/layout/ThemeProvider';
import Link from 'next/link';
import ThemeToggle from '../../components/ui/ThemeToggle';

const SobrePage = () => {
  const { theme } = useTheme();

  // Configurações de animação
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Define as cores baseadas no tema
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-700';
  const headingColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const bgColor = theme === 'dark' ? 'bg-gray-900' : 'bg-white';
  const cardBg = theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50';
  const blueSectionBg = theme === 'dark' ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50';

  return (
    <div className={`min-h-screen pt-16 ${bgColor}`}>
      {/* Botão para alternar tema */}
      <ThemeToggle showText={true} />
      
      {/* Breadcrumbs seguindo o padrão do site */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link 
                  href="/" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Início
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                  Sobre
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero section com título principal - mais compacto */}
      <section className={`relative py-12 overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'}`} aria-labelledby="about-heading">
        {/* Background elements */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 dark:opacity-5"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 dark:opacity-5"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h1 
            id="about-heading" 
            className={`text-3xl md:text-4xl font-bold mb-3 text-center ${headingColor}`}
          >
            Sobre a <span className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}>Web Imports</span>
          </h1>
          
          <p className={`text-lg text-center max-w-3xl mx-auto ${textColor}`}>
            Desde 2006 oferecendo soluções tecnológicas inovadoras para pessoas e empresas.
          </p>
        </div>
      </section>

      <div className={`container mx-auto px-4 py-8 ${bgColor}`}>
        {/* Nossa História - mais compacto */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-12"
        >
          <h2 className={`text-2xl font-bold mb-4 ${headingColor}`}>Nossa História</h2>
          <div className={`p-6 rounded-lg ${cardBg}`}>
            <p className={`text-base mb-3 ${textColor}`}>
              Fundada em 2006, a Web Imports nasceu com uma missão clara: oferecer produtos e soluções tecnológicas inovadoras para pessoas e empresas na região de Campinas. O que começou como uma pequena operação especializada em componentes eletrônicos, rapidamente evoluiu para um centro de soluções tecnológicas completas.
            </p>
            <p className={`text-base ${textColor}`}>
              Ao longo de mais de 17 anos de mercado, expandimos nosso portfólio de serviços, acompanhando as transformações do setor e as necessidades de nossos clientes. Do hardware ao software, da manutenção ao desenvolvimento, nossa trajetória reflete nosso compromisso com a excelência e a adaptabilidade.
            </p>
          </div>
        </motion.div>
        
        {/* Missão e Visão - mais compacto */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-12"
        >
          <h2 className={`text-2xl font-bold mb-4 ${headingColor}`}>Missão e Visão</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-lg ${blueSectionBg}`}>
              <h3 className={theme === 'dark' ? 'text-xl font-bold mb-3 text-blue-300' : 'text-xl font-bold mb-3 text-blue-700'}>Nossa Missão</h3>
              <p className={textColor}>
                Oferecer soluções tecnológicas personalizadas e de alta qualidade, promovendo transformação digital e eficiência operacional para nossos clientes, com atendimento humano e suporte excepcional.
              </p>
            </div>
            <div className={`p-6 rounded-lg ${blueSectionBg}`}>
              <h3 className={theme === 'dark' ? 'text-xl font-bold mb-3 text-blue-300' : 'text-xl font-bold mb-3 text-blue-700'}>Nossa Visão</h3>
              <p className={textColor}>
                Ser reconhecida como referência em soluções tecnológicas no interior de São Paulo, destacando-nos pela inovação, qualidade técnica e capacidade de antecipar as necessidades do mercado em constante evolução.
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* Nossos Valores - mais compacto */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-12"
        >
          <h2 className={`text-2xl font-bold mb-4 ${headingColor}`}>Nossos Valores</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className={`p-5 rounded-lg ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'border border-gray-200 bg-white'}`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className={`text-lg font-bold mb-2 ${headingColor}`}>Confiabilidade</h3>
              <p className={textColor}>
                Construímos relações sólidas baseadas em transparência e resultados consistentes.
              </p>
            </div>
            
            <div className={`p-5 rounded-lg ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'border border-gray-200 bg-white'}`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className={`text-lg font-bold mb-2 ${headingColor}`}>Inovação</h3>
              <p className={textColor}>
                Mantemo-nos na vanguarda tecnológica, buscando constantemente novas soluções e metodologias.
              </p>
            </div>
            
            <div className={`p-5 rounded-lg ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'border border-gray-200 bg-white'}`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className={`text-lg font-bold mb-2 ${headingColor}`}>Excelência</h3>
              <p className={textColor}>
                Comprometemo-nos com a qualidade em cada detalhe, garantindo resultados que superam expectativas.
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* Nossa Abordagem Tecnológica - mais compacto */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-12"
        >
          <h2 className={`text-2xl font-bold mb-4 ${headingColor}`}>Nossa Abordagem Tecnológica</h2>
          <div className={`p-6 rounded-lg ${cardBg}`}>
            <p className={`text-base mb-3 ${textColor}`}>
              Em um mundo onde a tecnologia evolui de forma cada vez mais rápida, nossa missão diária é manter-nos atualizados e preparados para os desafios do futuro. Investimos continuamente em capacitação e novas ferramentas, garantindo que nossos clientes tenham acesso às melhores e mais atuais soluções do mercado.
            </p>
            <p className={`text-base mb-3 ${textColor}`}>
              Acreditamos que a verdadeira inovação está na aplicação inteligente da tecnologia para resolver problemas reais. Por isso, cada projeto é desenvolvido com foco nas necessidades específicas do cliente, combinando expertise técnica com uma visão estratégica do negócio.
            </p>
            <p className={`text-base ${textColor}`}>
              De microsolda especializada a desenvolvimento web, de manutenção de dispositivos a infraestrutura de rede, nossa equipe multidisciplinar trabalha em sinergia para oferecer um portfólio completo de serviços tecnológicos.
            </p>
          </div>
        </motion.div>
        
        {/* Nossa Equipe - mais compacto */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-12"
        >
          <h2 className={`text-2xl font-bold mb-4 ${headingColor}`}>Nossa Equipe</h2>
          <div className={`p-6 rounded-lg ${cardBg}`}>
            <p className={`text-base mb-4 ${textColor}`}>
              Somos uma equipe apaixonada por tecnologia e resolução de problemas. Nossos especialistas combinam sólida formação técnica com experiência prática, sempre prontos para enfrentar os desafios mais complexos com criatividade e precisão.
            </p>
            
            <div className={`p-5 rounded-lg border-l-4 border-blue-500 italic ${theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <p className={`text-base ${theme === 'dark' ? 'text-white' : 'text-gray-700'}`}>
                &ldquo;O que nos diferencia é a combinação perfeita entre conhecimento técnico e atendimento humanizado. Acreditamos que a tecnologia deve aproximar pessoas, não afastá-las.&rdquo;
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* Call to Action - mais compacto */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className={`text-center py-12 px-6 rounded-lg ${theme === 'dark' ? 'bg-gradient-to-br from-blue-900 to-blue-800' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}
        >
          <h2 className={`text-2xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Vamos Construir o Futuro Juntos?</h2>
          <p className={`text-base mb-5 max-w-2xl mx-auto ${theme === 'dark' ? 'text-white' : 'text-gray-700'}`}>
            Desde 2006 ajudando pessoas e empresas a navegarem pelo mundo tecnológico. Estamos prontos para fazer parte da sua jornada.
          </p>
          <motion.a 
            href="/contato" 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-300 inline-block shadow-lg hover:shadow-xl"
          >
            Entre em Contato
          </motion.a>
        </motion.div>
      </div>
    </div>
  );
};

export default SobrePage;